/**
 * legalContentRender.js — Yasal belge (KVKK/Gizlilik/Şartlar) içerik render yardımcıları.
 *
 * TEK sanitize/render noktası: hem admin önizlemesi (AdminLegalContent.jsx) hem de
 * PUBLIC /legal/:docType sayfası (LegalPage.jsx) buradan import eder. Böylece iki
 * yerde ayrı ayrı (ve zamanla birbirinden sapabilecek) bir sanitize mantığı olmaz.
 *
 * Backend (routes/legal_content.py, bleach whitelist) ile MÜMKÜN OLDUĞUNCA hizalı:
 * aynı izinli tag seti, href/rel attribute'ları ve yalnızca https/mailto URL şemaları
 * (javascript:, data:, http:, vb. reddedilir). Backend zaten kayıt anında (PUT/publish)
 * bleach ile ayrıca sanitize ediyor (fail-closed); buradaki DOMPurify çağrısı ek bir
 * savunma katmanıdır (defense in depth) — DB'de zaten temiz içerik olsa da render
 * anında ikinci kez süzülür.
 */
import DOMPurify from 'dompurify';

// ── HTML escape (markdown render'da kullanıcı metnini önce kaçırmak için) ───────
export function escapeHtml(str) {
  return String(str || '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// ── DOMPurify whitelist — backend bleach whitelist ile hizalı ───────────────────
const SAFE_URI_REGEXP = /^(?:(?:https|mailto):|[^a-z]|[a-z+.\-]+(?:[^a-z+.\-:]|$))/i;

const DOMPURIFY_CONFIG = {
  ALLOWED_TAGS: [
    'p', 'br', 'strong', 'b', 'em', 'i', 'u', 'span',
    'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
    'ul', 'ol', 'li', 'a', 'blockquote', 'hr',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
  ],
  // 'class'/'target' yalnızca renderMarkdownPreview'in ürettiği sabit template'lerde
  // stil/anchor davranışı için kullanılır (kullanıcı girdisi değil); XSS riski taşımaz.
  ALLOWED_ATTR: ['href', 'rel', 'target', 'class'],
  ALLOWED_URI_REGEXP: SAFE_URI_REGEXP,
};

export function sanitizeHtmlPreview(html) {
  if (!html) return '';
  return DOMPurify.sanitize(String(html), DOMPURIFY_CONFIG);
}

/*
 * Markdown -> HTML.
 *
 * Desteklenen: baslik (h1-h3), madde listesi, tablo, blockquote, yatay cizgi,
 * bold/italic/link.
 *
 * ⚠️ SIRA ONEMLI — tum girdi ONCE `escapeHtml`'den geciyor. Yani bu fonksiyonun
 * gordugu metinde `>` karakteri artik `&gt;` olmus durumda; blockquote tespiti
 * bu yuzden `^&gt;` arar, `^>` DEGIL. `|` ve `-` escape edilmedigi icin tablo ve
 * yatay cizgi desenleri ham haliyle eslesir.
 *
 * Yeni etiket eklerken DOMPURIFY_CONFIG.ALLOWED_TAGS'e de eklenmeli, aksi halde
 * DOMPurify uretileni sessizce dusurur ve icerik "render edilmemis" gorunur.
 * `div` BILEREK yok: backend bleach whitelist'inde de yok, ikisi hizali kalmali —
 * bu yuzden tablolar sarmalayici bir div ile degil, kendi genisligiyle akar.
 */
export function renderMarkdownPreview(md) {
  if (!md) return '';
  const escaped = escapeHtml(md);
  const lines = escaped.split('\n');
  const htmlParts = [];
  let listBuffer = [];
  let tableBuffer = [];
  let quoteBuffer = [];

  const inline = (text) =>
    text
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      .replace(/\[(.+?)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer" class="underline text-[var(--brand-primary)]">$1</a>');

  const flushList = () => {
    if (listBuffer.length) {
      htmlParts.push(`<ul class="list-disc pl-5 space-y-1">${listBuffer.join('')}</ul>`);
      listBuffer = [];
    }
  };

  // ── Tablo ────────────────────────────────────────────────────────────────────
  const TABLO_SATIRI = /^\|.*\|$/;
  // Ayirici: yalnizca `-`, `:`, bosluk ve `|` iceren satir (`|---|---|`, `|:--|--:|`)
  const AYIRICI_SATIRI = /^\|[\s:|-]+\|$/;

  const hucreler = (satir) => satir.slice(1, -1).split('|').map((h) => h.trim());

  const flushTable = () => {
    if (!tableBuffer.length) return;
    const satirlar = tableBuffer;
    tableBuffer = [];

    let basliklar = null;
    let govde = satirlar;
    if (satirlar.length >= 2 && AYIRICI_SATIRI.test(satirlar[1])) {
      basliklar = hucreler(satirlar[0]);
      govde = satirlar.slice(2);
    }

    const thead = basliklar
      ? `<thead><tr>${basliklar
          .map((h) => `<th class="border border-black/10 px-3 py-2 text-left font-semibold align-top">${inline(h)}</th>`)
          .join('')}</tr></thead>`
      : '';
    const tbody = `<tbody>${govde
      .map((r) => `<tr>${hucreler(r)
        .map((h) => `<td class="border border-black/10 px-3 py-2 align-top break-words">${inline(h)}</td>`)
        .join('')}</tr>`)
      .join('')}</tbody>`;

    htmlParts.push(`<table class="w-full my-4 text-sm border-collapse">${thead}${tbody}</table>`);
  };

  // ── Blockquote ───────────────────────────────────────────────────────────────
  const flushQuote = () => {
    if (!quoteBuffer.length) return;
    const paragraflar = [];
    let cari = [];
    for (const q of quoteBuffer) {
      if (q === '') {
        if (cari.length) { paragraflar.push(cari.join(' ')); cari = []; }
      } else {
        cari.push(q);
      }
    }
    if (cari.length) paragraflar.push(cari.join(' '));
    quoteBuffer = [];
    htmlParts.push(
      `<blockquote class="border-l-4 border-black/15 pl-4 my-4 space-y-2">${paragraflar
        .map((p) => `<p>${inline(p)}</p>`)
        .join('')}</blockquote>`,
    );
  };

  const flushHepsi = () => { flushList(); flushTable(); flushQuote(); };

  for (const raw of lines) {
    const line = raw.trim();

    // Tablo ve blockquote cok satirli: once kendi tamponlarina toplanir.
    if (TABLO_SATIRI.test(line)) {
      flushList(); flushQuote();
      tableBuffer.push(line);
      continue;
    }
    if (/^&gt;/.test(line)) {
      flushList(); flushTable();
      quoteBuffer.push(line.replace(/^&gt;\s?/, ''));
      continue;
    }
    flushTable();
    flushQuote();

    if (/^(-{3,}|\*{3,}|_{3,})$/.test(line)) {
      // Yatay cizgi — liste desenlerinden ONCE denetlenir: `---` madde isareti degil.
      flushList();
      htmlParts.push('<hr class="my-6 border-black/10" />');
    } else if (/^###\s+/.test(line)) {
      flushList();
      htmlParts.push(`<h3 class="font-bold text-base mt-3 mb-1">${inline(line.replace(/^###\s+/, ''))}</h3>`);
    } else if (/^##\s+/.test(line)) {
      flushList();
      htmlParts.push(`<h2 class="font-bold text-lg mt-4 mb-1">${inline(line.replace(/^##\s+/, ''))}</h2>`);
    } else if (/^#\s+/.test(line)) {
      flushList();
      htmlParts.push(`<h1 class="font-bold text-xl mt-4 mb-2">${inline(line.replace(/^#\s+/, ''))}</h1>`);
    } else if (/^[-*]\s+/.test(line)) {
      listBuffer.push(`<li>${inline(line.replace(/^[-*]\s+/, ''))}</li>`);
    } else if (line === '') {
      flushList();
    } else {
      flushList();
      htmlParts.push(`<p class="mb-2">${inline(line)}</p>`);
    }
  }
  flushHepsi();
  // DOMPurify'dan geçir — inline() sadece markdown kalıplarından (**,*,[]()) üretilen
  // sabit template'ler ekliyor ve tüm kullanıcı metni escapeHtml ile önceden kaçırılmış
  // olsa da, tek sanitize noktası ilkesi gereği burada da aynı whitelist'ten geçirilir
  // (savunma derinliği).
  return DOMPurify.sanitize(htmlParts.join('\n'), DOMPURIFY_CONFIG);
}
